import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { pipelineApi } from '../../services/api';
import { formatAgency } from '../../utils/agency';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import { SkeletonRows } from '../../components/ui/Skeleton';
import { ArrowPathIcon, ExclamationTriangleIcon, FolderIcon } from 'components/icons';

/**
 * Every pursuit on one page, nearest obligation first. Each row is a
 * workspace; the "Needs you" column is the same next step the workspace
 * shows under its title, so the two never disagree.
 */

interface Row {
  id: string; stage: string; title: string; agency: string | null; solicitationNumber: string | null; samNoticeId: string;
  type: string | null; setAside: string | null; responseDeadline: string | null; deadlineTimeKnown: boolean;
  nextDue: { kind: string; requirement: string; deadline: string | null; daysRemaining: number | null } | null;
  soonestAt: string | null;
  nextStep: { key: string; title: string; blocking: boolean };
  verdict: string; unreadNotices: number;
  documents: { ready: number; total: number };
  checklist: { completed: number; total: number } | null;
  award: { awardee: string | null; amount: string | null } | null;
  estimatedValue: { amount: number; basis: string; recipient: string; awardId: string; endDate: string | null; url: string } | null;
  outcome: string | null; submittedAt: string | null; createdAt: string;
}

const STAGE_LABEL: Record<string, string> = {
  new: 'New', analyzing: 'Analyzing', qualified: 'Qualified', proposal: 'Proposal', submitted: 'Submitted', awarded: 'Awarded', lost: 'Lost', archived: 'Archived',
};
const STAGE_ORDER = ['new', 'analyzing', 'qualified', 'proposal', 'submitted', 'awarded', 'lost', 'archived'];
const CLOSED = new Set(['awarded', 'lost', 'archived']);
const KIND_LABEL: Record<string, string> = {
  site_visit: 'Site visit', conference: 'Conference', rsvp: 'RSVP', base_access: 'Base access', question_cutoff: 'Questions due', registration: 'Registration',
};

const money = (n: number) => n >= 1e6 ? `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n).toLocaleString()}`;
const BASIS_LABEL: Record<string, string> = {
  same_office_active: 'Current award from this office',
  same_office_recent: 'Last award from this office',
  agency_state_active: 'Current award, this agency in state',
  agency_state_recent: 'Recent award, this agency in state',
};
const monthYear = (d?: string | null) => d ? new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : '';

const fmtDate = (d?: string | null, timeKnown = true) => {
  if (!d) return '';
  const date = new Date(d);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', timeKnown
    ? { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) }
    : { month: 'short', day: 'numeric', timeZone: 'UTC', ...(sameYear ? {} : { year: 'numeric' }) });
};

/** Days until an instant, counting a bare date as that whole day. */
function daysUntil(iso: string | null, timeKnown = true): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  const end = timeKnown ? d.getTime() : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) - 1;
  return Math.ceil((end - Date.now()) / 86400000);
}

type Filter = 'all' | 'week' | 'needs' | 'past';
const FILTERS: Filter[] = ['all', 'week', 'needs', 'past'];

/**
 * `embedded` renders the list under the Pursuits page's own header (the
 * Board | List control lives there). `?filter=week|needs|past` narrows the
 * active rows, which is how the dashboard's tiles land on the right set.
 */
const Pursuits: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [params, setParams] = useSearchParams();
  const filter: Filter = FILTERS.includes(params.get('filter') as Filter) ? (params.get('filter') as Filter) : 'all';
  const setFilter = (f: Filter) => { const next = new URLSearchParams(params); if (f === 'all') next.delete('filter'); else next.set('filter', f); setParams(next, { replace: true }); };
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'deadline' | 'stage'>(() => { try { return (localStorage.getItem('clevor.pursuits.view') as any) || 'deadline'; } catch { return 'deadline'; } });
  const [showClosed, setShowClosed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await pipelineApi.listPursuits();
      setRows(r.data.data.rows);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not load your pursuits');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { try { localStorage.setItem('clevor.pursuits.view', view); } catch { /* ignore */ } }, [view]);

  const { active, closed, stats } = useMemo(() => {
    const all = rows || [];
    const active = all.filter((r) => !CLOSED.has(r.stage));
    const closed = all.filter((r) => CLOSED.has(r.stage));
    // Nearest obligation first; past dates after future ones; no date last.
    const key = (r: Row) => {
      const t = r.soonestAt ? new Date(r.soonestAt).getTime() : null;
      if (t === null) return [2, 0] as const;
      return t >= Date.now() ? [0, t] as const : [1, -t] as const;
    };
    active.sort((a, b) => { const ka = key(a), kb = key(b); return ka[0] - kb[0] || ka[1] - kb[1]; });
    const isWeek = (r: Row) => { const d = daysUntil(r.responseDeadline, r.deadlineTimeKnown); return d !== null && d >= 0 && d <= 7; };
    const isNeeds = (r: Row) => r.nextStep.blocking || r.unreadNotices > 0;
    const isPast = (r: Row) => { const d = daysUntil(r.responseDeadline, r.deadlineTimeKnown); return d !== null && d < 0; };
    const stats = { week: active.filter(isWeek).length, needs: active.filter(isNeeds).length, past: active.filter(isPast).length, active: active.length };
    const shown = filter === 'week' ? active.filter(isWeek) : filter === 'needs' ? active.filter(isNeeds) : filter === 'past' ? active.filter(isPast) : active;
    return { active: shown, closed, stats };
  }, [rows, filter]);

  const Due: React.FC<{ r: Row }> = ({ r }) => {
    const d = daysUntil(r.responseDeadline, r.deadlineTimeKnown);
    if (d === null) return <span className="text-content-muted">No date</span>;
    const tone = d < 0 ? 'text-content-muted' : d <= 2 ? 'text-danger' : d <= 7 ? 'text-warning' : 'text-content';
    const big = d < 0 ? `Closed ${fmtDate(r.responseDeadline, r.deadlineTimeKnown)}` : d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : `${d} days`;
    return (
      <span className={`block tabular-nums ${tone}`}>
        <span className="font-semibold">{big}</span>
        {d >= 0 && <span className="block text-caption text-content-muted">{fmtDate(r.responseDeadline, r.deadlineTimeKnown)}</span>}
        {r.nextDue && r.nextDue.daysRemaining !== null && r.nextDue.daysRemaining >= 0 && (
          <span className="block text-caption font-semibold text-warning">{KIND_LABEL[r.nextDue.kind] || 'Deadline'} {r.nextDue.daysRemaining === 0 ? 'today' : r.nextDue.daysRemaining === 1 ? 'tomorrow' : `in ${r.nextDue.daysRemaining} d`}</span>
        )}
      </span>
    );
  };

  const RowView: React.FC<{ r: Row }> = ({ r }) => (
    <Link to={`/pursuits/${r.id}`} className="grid grid-cols-1 gap-2 px-5 py-3.5 hover:bg-surface-sunk md:grid-cols-[minmax(0,1.4fr)_120px_minmax(0,1.2fr)_96px_110px] md:items-center md:gap-4">
      <div className="min-w-0">
        <div className="truncate text-body font-medium text-content">{r.title}</div>
        <div className="truncate text-caption text-content-muted">
          {[formatAgency(r.agency), r.solicitationNumber, r.setAside].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className="text-label"><Due r={r} /></div>
      <div className="min-w-0 text-label">
        <span className={`inline-flex items-start gap-1.5 ${r.nextStep.blocking ? 'text-warning' : 'text-content'}`}>
          {(r.nextStep.blocking || r.unreadNotices > 0) && <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 flex-none" />}
          <span className="line-clamp-2">{r.nextStep.title}</span>
        </span>
      </div>
      <div className="text-label md:text-right" title={r.estimatedValue ? `${BASIS_LABEL[r.estimatedValue.basis] || 'Prior award'}: ${r.estimatedValue.recipient}, ${r.estimatedValue.awardId}` : 'No comparable prior award found yet'}>
        {r.estimatedValue ? (
          <>
            <span className="font-semibold tabular-nums text-content">{money(r.estimatedValue.amount)}</span>
            <span className="block text-caption text-content-muted">{r.estimatedValue.basis.startsWith('same_office') ? 'this office' : 'agency'}{r.estimatedValue.basis.endsWith('active') && r.estimatedValue.endDate ? ` · ends ${monthYear(r.estimatedValue.endDate)}` : ''}</span>
          </>
        ) : <span className="text-content-subtle">—</span>}
      </div>
      <div className="flex items-center gap-2 text-caption text-content-muted md:justify-end">
        <span className="rounded-full bg-surface-sunk px-2 py-0.5 font-semibold text-content-muted">{STAGE_LABEL[r.stage] || r.stage}</span>
        {r.documents.total > 0 && <span className="tabular-nums" title="Attachments with text">{r.documents.ready}/{r.documents.total}</span>}
      </div>
    </Link>
  );

  const Header = () => (
    <div className="hidden grid-cols-[minmax(0,1.4fr)_120px_minmax(0,1.2fr)_96px_110px] gap-4 border-b border-border px-5 py-2 text-overline uppercase text-content-muted md:grid">
      <span>Pursuit</span><span>Due</span><span>Needs you</span><span className="text-right" title="Most relevant prior award for the same work, from USASpending">Prior award</span><span className="text-right">Stage</span>
    </div>
  );

  const toolbar = (
    <>
      <div role="group" aria-label="Order" className="flex overflow-hidden rounded-token border border-border text-label">
        {(['deadline', 'stage'] as const).map((v) => (
          <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)} className={`min-h-[40px] px-3.5 ${view === v ? 'bg-accent-sunk font-semibold text-accent' : 'text-content-muted hover:text-content'}`}>{v === 'deadline' ? 'By deadline' : 'By stage'}</button>
        ))}
      </div>
      <button type="button" onClick={load} aria-label="Refresh" className="inline-flex min-h-[40px] items-center rounded-token border border-border-strong bg-surface px-3 text-content hover:bg-surface-sunk"><ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
    </>
  );

  const tiles: Array<[Filter, string, number, string]> = [
    ['all', 'Active', stats.active, ''],
    ['week', 'Due this week', stats.week, stats.week ? 'text-warning' : ''],
    ['needs', 'Need you', stats.needs, stats.needs ? 'text-warning' : ''],
    ['past', 'Past the date', stats.past, stats.past ? 'text-content-muted' : ''],
  ];

  return (
    <div className="space-y-6">
      {embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-label text-content-muted">Nearest obligation first. Each row is its own workspace.</p>
          <div className="flex items-center gap-2">{toolbar}</div>
        </div>
      ) : (
        <PageHeader title="Pursuits" subtitle="Every solicitation you have added. Each row is its own workspace." actions={toolbar} />
      )}

      {rows && rows.length > 0 && (
        <div role="group" aria-label="Filter" className="flex flex-wrap gap-2 rounded-token-md border border-border bg-surface px-3 py-3 shadow-e1">
          {tiles.map(([f, k, v, tone]) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} className={`min-w-[7.5rem] rounded-token px-3 py-1.5 text-left ${filter === f ? 'bg-accent-sunk ring-1 ring-accent' : 'hover:bg-surface-sunk'}`}>
              <div className="text-overline uppercase text-content-muted">{k}</div>
              <div className={`text-stat tabular-nums ${tone}`}>{v}</div>
            </button>
          ))}
        </div>
      )}

      {loading && !rows ? (
        <div className="rounded-token-md border border-border bg-surface p-4"><SkeletonRows rows={5} /></div>
      ) : error ? (
        <ErrorState title="Couldn't load your pursuits" description={error} onRetry={load} retrying={loading} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState icon={FolderIcon} title="No pursuits yet" description="Add a solicitation to the pipeline from New matches or any opportunity page, and its workspace appears here." actionLabel="Find opportunities" actionHref="/opportunities" />
      ) : view === 'deadline' ? (
        <>
          <div className="rounded-token-md border border-border bg-surface shadow-e1">
            <Header />
            <div className="divide-y divide-border">
              {active.length === 0 && (
                <p className="px-5 py-6 text-body text-content-muted">
                  {filter === 'all' ? <>Nothing active. {closed.length} closed below.</> : <>Nothing {filter === 'week' ? 'due this week' : filter === 'needs' ? 'needs you right now' : 'past its date'}. <button type="button" onClick={() => setFilter('all')} className="text-accent hover:text-accent-hover">Show all</button></>}
                </p>
              )}
              {active.map((r) => <RowView key={r.id} r={r} />)}
            </div>
          </div>
          {closed.length > 0 && (
            <div className="rounded-token-md border border-border bg-surface shadow-e1">
              <button type="button" onClick={() => setShowClosed((v) => !v)} className="flex w-full items-center justify-between px-5 py-3 text-label font-medium text-content-muted hover:text-content">
                <span>{closed.length} closed (awarded, lost, archived)</span><span>{showClosed ? 'Hide' : 'Show'}</span>
              </button>
              {showClosed && <div className="divide-y divide-border border-t border-border">{closed.map((r) => <RowView key={r.id} r={r} />)}</div>}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-4">
          {STAGE_ORDER.filter((s) => (rows || []).some((r) => r.stage === s)).map((s) => {
            const group = [...active, ...closed].filter((r) => r.stage === s);
            return (
              <div key={s} className="rounded-token-md border border-border bg-surface shadow-e1">
                <div className="flex items-center justify-between border-b border-border px-5 py-2.5"><span className="text-overline uppercase text-content-muted">{STAGE_LABEL[s]}</span><span className="text-caption tabular-nums text-content-muted">{group.length}</span></div>
                <div className="divide-y divide-border">{group.map((r) => <RowView key={r.id} r={r} />)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Pursuits;
